import {Component, inject, signal, Signal} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {RouterModule} from '@angular/router';

import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {ContextService} from '../../services/context.service';
import {Organization} from '../../types-repository';
import {GET_ASPERA_ENDPOINTS} from '../../queries-aspera';
import {Endpoint} from '../../types-aspera';
import {DateComponent} from '../../date/date.component';

/** One row of the overview, the columns of the Signiant portal list: the same words for the same data. */
export interface EndpointListRow {
  id: string;
  name: string;
  /** The server and share, under the name. */
  share: string;
  /** What the "What we do" column shows, named from our side as the portal list does. Null: nothing here. */
  kind: 'Download' | 'Upload' | 'Both' | null;
  kindHint: string;
  session: 'logged-in' | 'mfa' | 'signed-out';
  account: string | null;
  lastSweepAt: string | null;
}

export function toEndpointRow(endpoint: Endpoint): EndpointListRow {
  // Presence of a capability is what it means to have it, so both can be set.
  const fetches = !!endpoint.download;
  const sends = !!endpoint.upload;
  const kind = fetches && sends ? 'Both' : sends ? 'Upload' : fetches ? 'Download' : null;
  const hint = fetches && sends
    ? 'We download from this share and upload to it'
    : sends ? 'We upload to this share' : fetches ? 'We download from this share' : 'Nothing in this environment';
  const status = endpoint.status;
  return {
    id: endpoint.id,
    name: endpoint.name,
    share: endpoint.shareId != null ? `${endpoint.server} · share ${endpoint.shareId}` : `${endpoint.server} (direct)`,
    kind,
    kindHint: hint,
    session: status.needsMfa ? 'mfa' : status.loggedIn ? 'logged-in' : 'signed-out',
    account: status.username ?? null,
    lastSweepAt: endpoint.lastSweepAt ?? null,
  };
}

@Component({
  selector: 'app-aspera-endpoints',
  templateUrl: './aspera-endpoints.component.html',
  styleUrls: ['./aspera-endpoints.component.scss'],
  imports: [LogoAreaComponent, HeadlineSectionComponent, RouterModule, DateComponent]
})
export class AsperaEndpointsComponent {
  organization: Signal<Organization>;
  endpoints = signal<EndpointListRow[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  private apollo = inject(Apollo);

  constructor(contextService: ContextService) {
    this.organization = contextService.organization;
    this.apollo.use('aspera')
      .query<{ endpoints: Endpoint[] }>({query: GET_ASPERA_ENDPOINTS, fetchPolicy: 'network-only'})
      .subscribe({
        next: ({data}) => {
          this.endpoints.set((data?.endpoints ?? []).map(toEndpointRow));
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err?.message ?? String(err));
          this.loading.set(false);
        }
      });
  }
}
